import {
    Button, ClickEvent, DataSource, DataSourceMode, EditRowDecorator, Event, Layout, TableRow, TableRowMode,
    ValidationResult
} from "@mcleod/components";
import { AutogenLayoutFuelSurchargeOptions } from "./autogen/AutogenLayoutFuelSurchargeOptions";
import { Api, ModelRow } from "@mcleod/core";
import { FuelSurchargeOptionsSlideout } from "./FuelSurchargeOptionsSlideout";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { FuelSurchargeRowDecorator } from "./FuelSurchargeRowDecorator";

export class FuelSurchargeOptions extends AutogenLayoutFuelSurchargeOptions {
    private _customerDataSource: DataSource;

    buttonEditOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableFuelSurcharge.findTableRowFromAdditionalTool(button);
        this.showFuelSurchargeTableRow(tableRow)
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        this.showFuelSurchargeTableRow(tableRow);
    }

    buttonDeleteOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableFuelSurcharge.findTableRowFromAdditionalTool(button);
        if (tableRow.data._appending) {
            tableRow.deleteRow();
        } else {
            Api.search("lme/ar/can-delete-customer-surcharge", { customer_surcharge_id: tableRow.data?.get("id") }).then(response => {
                tableRow.deleteRow();
            });
        }
    }

    async buttonAddSurchargeOnClick(event: ClickEvent) {
        const startingData: ModelRow = await this.tableFuelSurcharge._createNewRowData();
        startingData.set("customer_id", this.customerDataSource.activeRow?.get("id"));
        const layout = Layout.getLayout(this.tableFuelSurcharge.editLayout) as FuelSurchargeOptionsSlideout;
        const fsrd = new FuelSurchargeRowDecorator({
            layout: layout,
            title: "Add Fuel Surcharge",
            layoutLoadListeners: () => {
                layout.textboxFuelSurchargeId.enabled = true;
                layout.textboxFuelSurchargeId.required = true;
                layout.textboxSurchgStrtdt.required = true;
                layout.textboxDateOpt.required = true;
                layout.textboxApplyDoeRate.required = true;
                layout.textboxMethod.required = true;
                layout.currentTable = this.tableFuelSurcharge;
            },
            width: this.tableFuelSurcharge.editLayoutWidth,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            data: startingData,
            dataSourceMode: this.customerDataSource.mode,
            onDelete: null,
            onValidate: () => {
                return this.validateCharge(layout);
            },
            onSave: (updatedData: ModelRow | any) => {
                this.tableFuelSurcharge.dataSource.addRow(updatedData, this.sourceCustomerFuelOptions.data.length);
            },
            onAddAnother: () => {
                this.editClearNewRow(fsrd);
            }
        });
    }

    get customerDataSource(): DataSource {
        return this._customerDataSource;
    }

    set customerDataSource(value: DataSource) {
        this._customerDataSource = value;
    }


    showFuelSurchargeTableRow(tableRow: TableRow) {
        if (tableRow.mode === TableRowMode.ADD || tableRow.mode === TableRowMode.UPDATE) {
            const changesValid = tableRow.saveChanges();
            if (changesValid !== true)
                return;
        }
        const layout = Layout.getLayout(tableRow.table.editLayout) as FuelSurchargeOptionsSlideout;
        layout.currentTable = this.tableFuelSurcharge;
        const fsrd = new FuelSurchargeRowDecorator({
            layout: layout,
            title: "Edit Fuel Surcharge",
            layoutLoadListeners: () => {
                layout.textboxFuelSurchargeId.enabled = tableRow.mode === TableRowMode.ADD;
                tableRow.table.onEditLayoutLoaded != null ? (event: Event) => tableRow.table.onEditLayoutLoaded(fsrd, tableRow) : null
                layout.configureLayoutForSurchargeType("C");
            },
            width: tableRow.table.editLayoutWidth,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            data: tableRow.data.createBasicCopy(),
            dataSourceMode: tableRow.mode === TableRowMode.SEARCH ? DataSourceMode.SEARCH : DataSourceMode.UPDATE,
            onDelete: tableRow.mode !== TableRowMode.QUICK_ADD ? () => {
                tableRow.deleteRow(() => fsrd.slideOut());
            } : null,
            onSave: (updatedData: ModelRow | any) => tableRow.saveChangesFromEdit(updatedData),
            onClose: (cancelled: boolean) => tableRow.onSlideoutClose(fsrd, cancelled, false),
            onValidate: () => {
                return this.validateCharge(layout);
            },
        });
    }

    private validateCharge(layout: FuelSurchargeOptionsSlideout) {
        const validationResults: ValidationResult[] = layout.validate(true, true);
        const customValidation: ValidationResult = layout.customValidation();
        if (customValidation) {
            validationResults.push(customValidation);
        }
        if (!ValidationUtil.checkValidationResults(validationResults)) {
            ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid), validationResults.find(result => !result.isValid)?.validationMessage);
        }
        return validationResults;
    }

    editClearNewRow(fsrd: EditRowDecorator) {
        this.tableFuelSurcharge._createNewRowData().then(newRowData => {
            const addRowResult = this.tableFuelSurcharge.addRow(newRowData, { mode: TableRowMode.ADD }, {
                display: false,
                addToData: true
            });
            this.showEditLayout(true, addRowResult.row);
            this.onSlideoutClose(fsrd, false, true);
        });
    }

    onSlideoutClose(fsrd: FuelSurchargeRowDecorator, cancelled: boolean, addingRowInSlideout: boolean) {
        const slideout = () => fsrd.slideOut();
        if (cancelled === true && addingRowInSlideout === true) {
            this.tableFuelSurcharge.deleteRow(this.sourceCustomerFuelOptions.data.length - 1);
        }

        slideout();
    }

    showEditLayout(addingRowInSlideout: boolean = false, row: TableRow) {
        if (row.mode === TableRowMode.ADD || row.mode === TableRowMode.UPDATE) {
            const changesValid = row.saveChanges();
            if (changesValid !== true)
                return;
        }

        const fsrd = new FuelSurchargeRowDecorator({
            layout: Layout.getLayout(row.table.editLayout),
            layoutLoadListeners: row.table.onEditLayoutLoaded != null ? (event: Event) => row.table.onEditLayoutLoaded(fsrd, row) : null,
            width: row.table.editLayoutWidth,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            data: this._copyDataForSlideout(row),
            dataSourceMode: row.mode === TableRowMode.SEARCH ? DataSourceMode.SEARCH : DataSourceMode.UPDATE,
            onDelete: row.mode !== TableRowMode.QUICK_ADD ? () => {
                row.deleteRow(() => fsrd.slideOut());
            } : null,
            onSave: (updatedData: ModelRow | any) => row.saveChangesFromEdit(updatedData),
            onAddAnother: () => this.editClearNewRow(fsrd),
            onClose: (cancelled: boolean) => this.onSlideoutClose(fsrd, cancelled, addingRowInSlideout)
        });
    }

    private _copyDataForSlideout(row: TableRow): ModelRow | any {
        if (row.data instanceof ModelRow)
            return row.data.createBasicCopy();
        return row.data;
    }
}
